/**
 * @file Hand-over ("kiosk") session state — the exit guard's only store.
 * @module utils/forms/handoverSession
 *
 * F10 of ClickUp 86d3t4ev9. A practitioner hands their tablet to the patient so the patient
 * fills the form themselves; a 4-digit PIN brings the practitioner back to where they were.
 *
 * ═══ WHAT THIS GUARD IS, AND IS NOT ══════════════════════════════════════════════════════
 * IT IS A SOFT GUARD. There is exactly ONE session per browser — identity's HttpOnly session
 * cookies, sent with every same-origin request. Nothing here degrades or scopes that session. A patient who opens a
 * new tab, types the base URL, or uses an OS app-switcher reaches the full clinical app with
 * the practitioner still signed in.
 *
 * What this DOES buy: the handed-over view has no sidebar, no topbar, no notifications and no
 * Xoe widget; Back and reload do not silently drop the patient into the clinical app; and
 * leaving deliberately costs a PIN. That is a meaningful reduction in accidental exposure and
 * nothing more. A hard guarantee needs a separate short-lived credential issued for the
 * hand-over window, which is a much larger change and is NOT what this is.
 *
 * ═══ WHY THE PIN IS HASHED, AND WHAT THAT IS WORTH ═══════════════════════════════════════
 * The PIN itself is never stored. Only `sha256(salt + pin)` and the random salt go to
 * `sessionStorage` — never `localStorage`, never a cookie, never the URL.
 *
 * ⚠️ BE HONEST ABOUT THE STRENGTH: a 4-digit PIN has 10,000 candidates. Anyone who can read
 * `sessionStorage` can also run 10,000 hashes, so this is NOT a secret store and must never
 * be described as one. It exists so the PIN is not readable at a glance — from a devtools
 * panel, a screen recording, or a support bundle — and so it cannot be copied verbatim into
 * another device. The lockout below is what actually costs an attacker something, and it is
 * deliberately the *safe* failure: a locked-out device is fine, a guessable exit is not.
 *
 * `sessionStorage` (not `localStorage`) is also load-bearing: it is per-tab and dies with the
 * tab, so an abandoned hand-over cannot leave a stale guard behind on the next session. It
 * survives a reload of the same tab, which is exactly the requirement.
 *
 * ═══ WHY SHA-256 IS IMPLEMENTED HERE RATHER THAN CALLING `crypto.subtle` ═════════════════
 * `crypto.subtle` is undefined outside a secure context, and local development is served over
 * plain `http://{MINIKUBE_IP}:3000`. A `crypto.subtle` implementation would therefore work in
 * production and throw in dev — the worst possible split. This is ~50 lines, synchronous,
 * deterministic, and behaves identically in a browser and under jsdom.
 */

// ─── SHA-256 ──────────────────────────────────────────────────────────────────────────────

const K = [
	0x428a2f98, 0x71374491, 0xb5c0fbcf, 0xe9b5dba5, 0x3956c25b, 0x59f111f1, 0x923f82a4, 0xab1c5ed5,
	0xd807aa98, 0x12835b01, 0x243185be, 0x550c7dc3, 0x72be5d74, 0x80deb1fe, 0x9bdc06a7, 0xc19bf174,
	0xe49b69c1, 0xefbe4786, 0x0fc19dc6, 0x240ca1cc, 0x2de92c6f, 0x4a7484aa, 0x5cb0a9dc, 0x76f988da,
	0x983e5152, 0xa831c66d, 0xb00327c8, 0xbf597fc7, 0xc6e00bf3, 0xd5a79147, 0x06ca6351, 0x14292967,
	0x27b70a85, 0x2e1b2138, 0x4d2c6dfc, 0x53380d13, 0x650a7354, 0x766a0abb, 0x81c2c92e, 0x92722c85,
	0xa2bfe8a1, 0xa81a664b, 0xc24b8b70, 0xc76c51a3, 0xd192e819, 0xd6990624, 0xf40e3585, 0x106aa070,
	0x19a4c116, 0x1e376c08, 0x2748774c, 0x34b0bcb5, 0x391c0cb3, 0x4ed8aa4a, 0x5b9cca4f, 0x682e6ff3,
	0x748f82ee, 0x78a5636f, 0x84c87814, 0x8cc70208, 0x90befffa, 0xa4506ceb, 0xbef9a3f7, 0xc67178f2,
];

const rotr = (x: number, n: number): number => ((x >>> n) | (x << (32 - n))) >>> 0;

/** Lower-case hex SHA-256 of a UTF-8 string. */
export const sha256Hex = (input: string): string => {
	const message = new TextEncoder().encode(input);
	const bitLength = message.length * 8;
	// message + 0x80 + zero padding + 8-byte length, rounded up to a whole number of blocks.
	const padded = new Uint8Array(Math.ceil((message.length + 9) / 64) * 64);
	padded.set(message);
	padded[message.length] = 0x80;

	const view = new DataView(padded.buffer);
	view.setUint32(padded.length - 8, Math.floor(bitLength / 0x100000000));
	view.setUint32(padded.length - 4, bitLength >>> 0);

	const h = new Uint32Array([
		0x6a09e667, 0xbb67ae85, 0x3c6ef372, 0xa54ff53a, 0x510e527f, 0x9b05688c, 0x1f83d9ab, 0x5be0cd19,
	]);
	const w = new Uint32Array(64);

	for (let offset = 0; offset < padded.length; offset += 64) {
		for (let i = 0; i < 16; i++) w[i] = view.getUint32(offset + i * 4);
		for (let i = 16; i < 64; i++) {
			const s0 = rotr(w[i - 15], 7) ^ rotr(w[i - 15], 18) ^ (w[i - 15] >>> 3);
			const s1 = rotr(w[i - 2], 17) ^ rotr(w[i - 2], 19) ^ (w[i - 2] >>> 10);
			w[i] = (w[i - 16] + s0 + w[i - 7] + s1) >>> 0;
		}

		let a = h[0], b = h[1], c = h[2], d = h[3], e = h[4], f = h[5], g = h[6], hh = h[7];

		for (let i = 0; i < 64; i++) {
			const S1 = rotr(e, 6) ^ rotr(e, 11) ^ rotr(e, 25);
			const ch = (e & f) ^ (~e & g);
			const t1 = (hh + S1 + ch + K[i] + w[i]) >>> 0;
			const S0 = rotr(a, 2) ^ rotr(a, 13) ^ rotr(a, 22);
			const maj = (a & b) ^ (a & c) ^ (b & c);
			const t2 = (S0 + maj) >>> 0;
			hh = g; g = f; f = e; e = (d + t1) >>> 0; d = c; c = b; b = a; a = (t1 + t2) >>> 0;
		}

		h[0] = (h[0] + a) >>> 0; h[1] = (h[1] + b) >>> 0; h[2] = (h[2] + c) >>> 0; h[3] = (h[3] + d) >>> 0;
		h[4] = (h[4] + e) >>> 0; h[5] = (h[5] + f) >>> 0; h[6] = (h[6] + g) >>> 0; h[7] = (h[7] + hh) >>> 0;
	}

	let out = "";
	for (let i = 0; i < 8; i++) out += h[i].toString(16).padStart(8, "0");
	return out;
};

// ─── Session shape & constants ────────────────────────────────────────────────────────────

/** The `?mode=` value that puts `/patient/form` into hand-over mode. */
export const HANDOVER_MODE = "handover";
export const HANDOVER_MODE_PARAM = "mode";

export const HANDOVER_PIN_LENGTH = 4;
export const HANDOVER_MAX_ATTEMPTS = 5;

/** Key in `sessionStorage`. Nothing else in the app writes it. */
export const HANDOVER_SESSION_KEY = "mg-handover-session";

export interface HandoverSession {
	/** Bumped if the shape ever changes; an unrecognised version is discarded, not migrated. */
	v: 1;
	/** The assignment that was handed over. The guard only arms for this id. */
	assignedFormId: number;
	/** Random per-hand-over salt. Hex. */
	salt: string;
	/** `sha256(salt + pin)`. The PIN itself never leaves the input element. */
	hash: string;
	/** Where the practitioner was, so exit puts them back. Path + query, same-origin. */
	returnPath: string;
	/** Failed unlock attempts so far. Persisted so a reload cannot reset it. */
	attempts: number;
	/** Set once `attempts` reaches `HANDOVER_MAX_ATTEMPTS`; only a full logout clears it. */
	lockedOut: boolean;
}

const makeSalt = (): string => {
	const bytes = new Uint8Array(8);
	const webcrypto = typeof globalThis !== "undefined" ? (globalThis.crypto as Crypto | undefined) : undefined;
	if (webcrypto && typeof webcrypto.getRandomValues === "function") {
		webcrypto.getRandomValues(bytes);
	} else {
		// `getRandomValues` is available in insecure contexts and under jsdom, so this is a
		// belt-and-braces branch. It is not a security downgrade that matters here: the salt
		// only stops one device's stored hash being replayed against another's.
		for (let i = 0; i < bytes.length; i++) bytes[i] = Math.floor(Math.random() * 256);
	}
	let hex = "";
	for (let i = 0; i < bytes.length; i++) hex += bytes[i].toString(16).padStart(2, "0");
	return hex;
};

// ─── Store ────────────────────────────────────────────────────────────────────────────────
//
// Every access is wrapped: `sessionStorage` throws in Safari private mode and when storage is
// disabled by policy, and a hand-over that cannot store its guard must fail visibly at the
// start rather than half-arm.

const readRaw = (): string | null => {
	try {
		return window.sessionStorage.getItem(HANDOVER_SESSION_KEY);
	} catch {
		return null;
	}
};

const writeRaw = (session: HandoverSession): boolean => {
	try {
		window.sessionStorage.setItem(HANDOVER_SESSION_KEY, JSON.stringify(session));
		return true;
	} catch {
		return false;
	}
};

export const readHandoverSession = (): HandoverSession | null => {
	const raw = readRaw();
	if (!raw) return null;
	try {
		const parsed = JSON.parse(raw) as HandoverSession;
		if (!parsed || parsed.v !== 1 || typeof parsed.hash !== "string" || typeof parsed.salt !== "string") {
			return null;
		}
		return parsed;
	} catch {
		return null;
	}
};

export const clearHandoverSession = (): void => {
	try {
		window.sessionStorage.removeItem(HANDOVER_SESSION_KEY);
	} catch {
		/* nothing to clear if storage is unavailable */
	}
};

/**
 * Arm the guard. Returns false when the session could not be stored — the caller MUST then
 * abandon the hand-over rather than navigate, because an unarmed guard is a chrome-free form
 * with no PIN on the way out.
 */
export const startHandoverSession = ({
	assignedFormId,
	pin,
	returnPath,
}: {
	assignedFormId: number;
	pin: string;
	returnPath: string;
}): boolean => {
	const salt = makeSalt();
	return writeRaw({
		v: 1,
		assignedFormId,
		salt,
		hash: sha256Hex(salt + pin),
		returnPath,
		attempts: 0,
		lockedOut: false,
	});
};

export type HandoverUnlockResult = "unlocked" | "wrong" | "locked" | "no-session";

/**
 * Check a PIN and record the attempt. The attempt counter is written BEFORE the answer is
 * returned, so a caller that crashes mid-dialog still burns the attempt.
 */
export const verifyHandoverPin = (pin: string): HandoverUnlockResult => {
	const session = readHandoverSession();
	if (!session) return "no-session";
	if (session.lockedOut) return "locked";

	if (sha256Hex(session.salt + pin) === session.hash) return "unlocked";

	const attempts = session.attempts + 1;
	const lockedOut = attempts >= HANDOVER_MAX_ATTEMPTS;
	writeRaw({ ...session, attempts, lockedOut });
	return lockedOut ? "locked" : "wrong";
};

// ─── Navigation ───────────────────────────────────────────────────────────────────────────

let exiting = false;

/** True between the decision to leave hand-over and the page actually unloading. */
export const isHandoverExiting = (): boolean => exiting;

/**
 * The one place this feature touches `window.location`.
 *
 * It is an object rather than a bare call so tests have somewhere to stand: jsdom defines
 * `window.location` as non-configurable and `Location.assign` as read-only, so neither
 * `jest.spyOn(window.location, "assign")` nor replacing `window.location` works — the only
 * way to assert "the app navigated HERE" is to own the seam.
 */
export const handoverNavigator = {
	go: (url: string): void => {
		window.location.assign(url);
	},
};

/**
 * Leave hand-over mode.
 *
 * A FULL page load, not a router transition, and that is the point: the clinical SPA is torn
 * down and rebuilt, so nothing the practitioner had in memory (React Query caches of patient
 * lists, open drawers, half-typed notes) survives the moment the tablet changes hands, and
 * the list they come back to is refetched rather than restored.
 *
 * `exiting` is set first so the guard's `beforeunload` handler stands down — otherwise every
 * deliberate exit would raise the browser's "leave site?" prompt at the patient.
 */
export const exitHandover = (url: string): void => {
	exiting = true;
	clearHandoverSession();
	handoverNavigator.go(url);
};

/**
 * Enter hand-over mode. Same full-load reasoning as `exitHandover`, in the other direction:
 * the clinical app must be gone from memory before the device is handed over, and a router
 * transition would leave it mounted-but-unrendered one history entry away.
 */
export const enterHandover = (url: string): void => {
	handoverNavigator.go(url);
};

/** Test seam only — resets the module-level exit latch between cases. */
export const resetHandoverExitFlagForTests = (): void => {
	exiting = false;
};

// ─── Route predicate ──────────────────────────────────────────────────────────────────────

/**
 * Is this location the handed-over form?
 *
 * A pure function for the same reason `resolvePatientFormRoute` is one: its only consumer is
 * `App.tsx`, and `src/App.test.tsx` is excluded from `jest.config.js` (it needs Vite's
 * `import.meta.env`), so a predicate that lives there cannot be tested.
 */
export const isHandoverRoute = (pathname: string, search: string): boolean => {
	if (pathname !== "/patient/form") return false;
	return new URLSearchParams(search).get(HANDOVER_MODE_PARAM) === HANDOVER_MODE;
};
